import { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Field } from '@navozyme/uikit/dist/atoms/Field';
import { DatePickerTimeInput } from '@navozyme/uikit/dist/molecules/DatePicker';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import diff from 'object-diff';
import * as Yup from 'yup';

import { AppDispatch } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';

import {
  searchPort,
  selectSearchPortsResults,
  State,
} from '../../../../forms/components/search-ports-dropdown/slices/searchPortsSlice';
import {
  createPortcallStatusLog,
  getInspections,
  getPortcallStatusLog,
  PortData,
  selectInspections,
  updateInspections,
} from '../../../slices/portCallSlice';

import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import SearchPortsDropdown from '../../../../forms/components/search-ports-dropdown/components/SearchPortsDropdown';
import Textarea from '../../../../forms/components/TextArea';
import { booleanOptionsForRadioGroup } from '../../../../forms/helpers/random-form-helpers';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import { API_REQUEST_FORMAT_DATE } from '../../../../global/utils/datetimes';
import Page from '../../../../layout/components/Page';
import PageHeader from '../../../../layout/components/PageHeader';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';

import '../styles/inspections.scss';

const validationSchema = Yup.object({});

interface InspectionsValues {
  isSubjectedToExpandedInspection: boolean;
  portLastExpandedInspection?: string | null;
  dateLastExpandedInspection?: Date | string | null;
  dropdownPortLastExpandedInspection?: PortData;
  plannedStatutorySurveys?: string;
}

const Inspections = () => {
  const formRef = useRef(null);
  const { formContextRef } = useContext(FormRefContext);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params = useParams();
  const dropdownIDToInitializePort = 'portLastExpandedInspectionInitial';
  const searchPortsResults =
    useSelector((state: State) =>
      selectSearchPortsResults(state, dropdownIDToInitializePort)
    ) || [];
  const inspections = useSelector(selectInspections);
  const unlocode = inspections?.data?.portLastExpandedInspection;
  const hasToRetrievePort = unlocode && unlocode.length === 5;
  const hasToInitializePort =
    hasToRetrievePort && searchPortsResults.length === 1;
  const mouInspectionOptions = booleanOptionsForRadioGroup();

  useEffect(() => {
    formContextRef.current = formRef.current;
  }, [formContextRef, formRef, inspections]);

  useEffect(() => {
    params.uid && dispatch(getInspections(params.uid));
  }, [dispatch, params.uid]);

  useEffect(() => {
    if (hasToRetrievePort) {
      dispatch(
        searchPort({
          dropdownID: dropdownIDToInitializePort,
          searchData: { unlocode: unlocode },
        })
      );
    }
  }, [
    dispatch,
    hasToRetrievePort,
    inspections.data?.portLastExpandedInspection,
    unlocode,
  ]);

  const initialValues = () => {
    const values = {
      ...inspections.data,
      isSubjectedToExpandedInspection:
        inspections?.data?.isSubjectedToExpandedInspection || false,
      dateLastExpandedInspection: inspections?.data?.dateLastExpandedInspection
        ? new Date(inspections.data.dateLastExpandedInspection)
        : null,
    };
    if (hasToInitializePort) {
      values.dropdownPortLastExpandedInspection = searchPortsResults[0];
    }
    return values;
  };

  const handleSubmit = async (values: InspectionsValues) => {
    const changedValues = diff(initialValues(), values);

    if (Object.keys(changedValues).length) {
      const finalValues = { ...values };
      if (values.isSubjectedToExpandedInspection) {
        if (values.dateLastExpandedInspection) {
          finalValues.dateLastExpandedInspection = format(
            new Date(values.dateLastExpandedInspection),
            API_REQUEST_FORMAT_DATE
          );
        } else {
          delete finalValues.dateLastExpandedInspection;
        }

        if (values.dropdownPortLastExpandedInspection) {
          finalValues.portLastExpandedInspection =
            values.dropdownPortLastExpandedInspection.unlocode;
          delete finalValues.dropdownPortLastExpandedInspection;
        }
      } else {
        finalValues.isSubjectedToExpandedInspection =
          values.isSubjectedToExpandedInspection;
        delete finalValues.dateLastExpandedInspection;
        delete finalValues.portLastExpandedInspection;
        delete finalValues.plannedStatutorySurveys;
      }
      if (params.uid) {
        const isSuccess = await dispatch(
          updateInspections({
            uid: params.uid,
            data: finalValues,
          })
        ).then((response) => {
          if (response.type && response.type.endsWith('/rejected'))
            return false;
          else return true;
        });
        if (!isSuccess) return;
      }
    }

    if (params.uid) {
      const isSuccessUpdateStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: DossierSectionOptions.GeneralDeclarationInspections,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccessUpdateStatus) return;

      dispatch(getPortcallStatusLog(params.uid));
    }

    navigate(`/${params.uid}/cargo`);
  };

  const maxDate = new Date();

  return (
    <Page>
      <div className="inspections">
        <PageHeader title={'Inspections'} />
        <div className="warning">
          <p className="warning__title">
            Ships subjected to a MOU expanded inspection:
          </p>
          <ul>
            <li>All ships with a high risk profile </li>
            <li>
              Passenger ships, oil tankers, gas carriers, chemical tankers or
              bulk carriers, older than 12 years of age.
            </li>
          </ul>
        </div>
        <Formik
          innerRef={formRef}
          onSubmit={handleSubmit}
          initialValues={initialValues()}
          enableReinitialize
          validationSchema={validationSchema}>
          {({ values, setFieldValue, errors }) => (
            <Form className="inspactions-form">
              <div className="inspactions-form__controls">
                <div className="inspactions-form__first-row">
                  <RadioButtonsCustom
                    name="isSubjectedToExpandedInspection"
                    label="Is the ship subjected to MOU expanded inspection? *"
                    options={mouInspectionOptions}
                  />
                </div>
                {values.isSubjectedToExpandedInspection ? (
                  <>
                    <div className="inspactions-form__second-row">
                      <Field
                        label="Date of last expanded inspection"
                        className="field_date_picker">
                        <DatePickerTimeInput
                          key={
                            initialValues()?.dateLastExpandedInspection +
                            'dateLastExpandedInspection'
                          }
                          name="dateLastExpandedInspection"
                          sizeInput="large"
                          maxDate={maxDate}
                          showTime={false}
                          defaultValue={
                            initialValues()?.dateLastExpandedInspection
                          }
                          action={({ isoDate }: { isoDate: string }) =>
                            setFieldValue(
                              'dateLastExpandedInspection',
                              new Date(isoDate)
                            )
                          }
                          hasError={errors?.dateLastExpandedInspection}
                          errorMessage={errors?.dateLastExpandedInspection}
                        />
                      </Field>
                      <SearchPortsDropdown
                        name="dropdownPortLastExpandedInspection"
                        dropdownID="portLastExpandedInspection"
                        label="Port where it was carried out"
                        placeholder="Carried Out Port"
                      />
                    </div>
                    <div className="inspactions-form__third-row">
                      <Textarea
                        name="plannedStatutorySurveys"
                        rows={10}
                        placeholder="Write here."
                        label="Planned statutory surveys / Substantial maintenance and repair work"
                      />
                    </div>
                  </>
                ) : null}
              </div>
              <SkipAndNextButtons
                next={`/${params.uid}/cargo`}
                back={`/${params.uid}/vessel-data`}
                submitDisabled={false}
              />
            </Form>
          )}
        </Formik>
      </div>
    </Page>
  );
};

export default Inspections;
